/**
 * Refer-a-friend (#186): the `?ref=CODE` a friend's share link lands with.
 *
 * Captured on any web-app page load and sent once at signup (`referralCode`), where the
 * backend resolves it to the referrer. The first link clicked wins; a stale capture
 * expires after the window below so an old click never claims a much later signup.
 */
import { trackReferralLink } from '@common-reducers/backend-api/backend-api';

const STORAGE_KEY = 'blueticks_referral';
const MAX_AGE_MS = 30 * 24 * 60 * 60 * 1000;
/** Mirrors the backend alphabet (no 0/O, 1/I/L). */
const CODE_RE = /^[ABCDEFGHJKMNPQRSTUVWXYZ23456789]{8}$/;

interface StoredReferral { code: string; at: number }

export function normalizeReferralCode(value: unknown): string | undefined {
  if (typeof value !== 'string') return undefined;
  const code = value.trim().toUpperCase();
  return CODE_RE.test(code) ? code : undefined;
}

function readStored(now: number): StoredReferral | undefined {
  try {
    const parsed = JSON.parse(localStorage.getItem(STORAGE_KEY) || 'null') as StoredReferral | null;
    if (parsed && normalizeReferralCode(parsed.code) && now - parsed.at < MAX_AGE_MS) return parsed;
  } catch {
    // unreadable storage → no referral
  }
  return undefined;
}

const VISITOR_KEY = 'blueticks_referral_visitor';
/** Backend contract: 16-64 chars, [A-Za-z0-9-]. */
const VISITOR_RE = /^[A-Za-z0-9-]{16,64}$/;
const ALPHANUM = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';

function newVisitorKey(): string {
  const c = (globalThis as { crypto?: { randomUUID?: () => string } }).crypto;
  if (typeof c?.randomUUID === 'function') return c.randomUUID();
  let key = '';
  for (let i = 0; i < 32; i++) key += ALPHANUM[Math.floor(Math.random() * ALPHANUM.length)];
  return key;
}

/** Random per-browser key for the anonymous `visited` ping. Not tied to the user. */
export function getOrCreateVisitorKey(): string | undefined {
  try {
    const existing = localStorage.getItem(VISITOR_KEY);
    if (existing && VISITOR_RE.test(existing)) return existing;
    const key = newVisitorKey();
    localStorage.setItem(VISITOR_KEY, key);
    return key;
  } catch {
    return undefined;
  }
}

export function captureReferralFromUrl(search: string = window.location.search, now: number = Date.now()): void {
  let newCode: string | undefined;
  try {
    const code = normalizeReferralCode(new URLSearchParams(search).get('ref'));
    if (!code || readStored(now)) return;
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ code, at: now }));
    newCode = code;
  } catch {
    // storage blocked → signup simply carries no referral
  }
  // Only a newly stored code is a visit; a repeat landing while a fresh code is held is not counted.
  if (newCode) {
    const visitorKey = getOrCreateVisitorKey();
    if (visitorKey) trackReferralLink({ kind: 'visited', code: newCode, visitorKey });
  }
}

export function readReferralCode(now: number = Date.now()): string | undefined {
  return readStored(now)?.code;
}
